import { useLocation, useHistory } from "react-router-dom";
import { motion } from "framer-motion";
import { IonIcon } from "@ionic/react";
import { home, person, search, cart } from "ionicons/icons";
import React, { useState, useRef } from "react";
import { useEventListener, useRafState, useMemoizedFn } from "ahooks";
import { useAuth } from "../../../../contexts/auth-context";
import { useCartData } from "../../../../providers/cartDataStore";
import { prettifyNumbers } from "../../../../utils/prettifyNumbers";
import { useConfig } from "../../../../providers/config";
import { useOrderData } from "../../../../providers/orderDataStore";
import { useChatsUnreadCount } from "../../../../providers/Chat";
import ChatUnreadBadge from "../ChatUnreadBadge";

const TAB_WIDTH = 70;
const TAB_GAP = 7;
const CONTAINER_PADDING = 8;
const BUBBLE_WIDTH = TAB_WIDTH + 10;
const DRAG_THRESHOLD_PX = 8;

const getTabCenterX = (index: number) => CONTAINER_PADDING + index * (TAB_WIDTH + TAB_GAP) + TAB_WIDTH / 2;
const DISPLAY_PATH = ["search-main-categories"];
const ORDER_CHAT_TABBAR_PATH = /^\/orders\/[^/]+\/chat\/?$/;

const CART_TAB_INDEX = 2;
const PROFILE_TAB_INDEX = 3;

interface TabItem {
  icon: string;
  to: string;
  activeIcon: string;
  label: string;
  altRoute?: string;
  notification?: number;
  onClick: () => void;
}

const TabNavigation = () => {
  const config = useConfig();
  const { user } = useAuth();
  const history = useHistory();
  const location = useLocation();
  const customerId = user ? Number(user.id) || undefined : undefined;
  const isOrderChatEnabled = !!config.isOrderChatEnabled;
  const chatsUnreadCount = useChatsUnreadCount({ customerId, isOrderChatEnabled });
  const shouldHideButton =
    DISPLAY_PATH.some((path) => location.pathname.includes(path)) || ORDER_CHAT_TABBAR_PATH.test(location.pathname);

  const TABS: TabItem[] = [
    {
      to: "/home3",
      icon: home,
      activeIcon: home,
      label: "Главная",
      altRoute: "/promo",
      onClick: () => history.push("/home3"),
    },
    {
      to: "/search-main-categories",
      icon: search,
      activeIcon: search,
      label: "Поиск",
      altRoute: "/categories",
      onClick: () => history.push("/search-main-categories"),
    },
    { to: "/cart", icon: cart, activeIcon: cart, label: "Корзина", onClick: () => history.push("/cart") },
    {
      to: "/my-account",
      icon: person,
      activeIcon: person,
      label: "Профиль",
      altRoute: "/auth",
      onClick: () => {
        history.push(user ? "/my-account" : "/auth");
      },
    },
  ];

  const getActiveTabIndex = (pathname: string) =>
    TABS.findIndex((tab) => pathname.startsWith(tab.to) || (tab.altRoute && pathname.startsWith(tab.altRoute)));

  const clampBubbleX = (x: number) => Math.max(getTabCenterX(0), Math.min(getTabCenterX(TABS.length - 1), x));

  const getNearestTabIndex = (centerX: number) => {
    let nearest = 0;
    let minDist = Infinity;
    TABS.forEach((_, i) => {
      const dist = Math.abs(centerX - getTabCenterX(i));
      if (dist < minDist) {
        minDist = dist;
        nearest = i;
      }
    });
    return nearest;
  };

  const { cartProducts, cartTotalValue } = useCartData();
  const { shippingMethod } = useOrderData();
  const currentShippingCost = shippingMethod?.cost || "0";

  const cartCount = cartProducts.reduce((acc, item) => acc + item.quantity, 0);

  const cartCountFormatted = prettifyNumbers(cartCount);
  const cartTotalValueWithShipping = prettifyNumbers(cartTotalValue + parseInt(currentShippingCost));

  const barRef = useRef<HTMLDivElement>(null);
  const tabsContainerRef = useRef<HTMLDivElement>(null);

  const [isDragging, setIsDragging] = useState(false);
  const [bubbleCenterX, setBubbleCenterX] = useRafState<number | null>(null);
  const bubbleCenterXRef = useRef<number | null>(null);
  const dragStartRef = useRef<{
    clientX: number;
    bubbleCenterX: number;
    tappedTabIndex: number;
    captureTarget: HTMLElement;
  } | null>(null);

  const activeIndex = Math.max(0, getActiveTabIndex(location.pathname));
  const defaultBubbleX = getTabCenterX(activeIndex);

  const handlePointerDown = useMemoizedFn((e: PointerEvent) => {
    if (!tabsContainerRef.current) return;
    const rect = tabsContainerRef.current.getBoundingClientRect();
    if (e.clientX < rect.left || e.clientX > rect.right) return;
    if (e.clientY < rect.top || e.clientY > rect.bottom) return;

    e.preventDefault();
    e.stopPropagation();
    const target = e.target as HTMLElement;
    target.setPointerCapture?.(e.pointerId);
    const xInContainer = e.clientX - rect.left;
    const tappedIndex = getNearestTabIndex(xInContainer);
    const currentBubbleX = bubbleCenterX ?? getTabCenterX(activeIndex);
    dragStartRef.current = {
      clientX: e.clientX,
      bubbleCenterX: currentBubbleX,
      tappedTabIndex: tappedIndex,
      captureTarget: target,
    };
    bubbleCenterXRef.current = currentBubbleX;
    setIsDragging(true);
    setBubbleCenterX(currentBubbleX);
  });

  const handlePointerMove = useMemoizedFn((e: PointerEvent) => {
    if (!dragStartRef.current) return;
    const deltaX = e.clientX - dragStartRef.current.clientX;
    const newX = clampBubbleX(dragStartRef.current.bubbleCenterX + deltaX);
    bubbleCenterXRef.current = newX;
    setBubbleCenterX(newX);
  });

  const handlePointerUp = useMemoizedFn((e: PointerEvent) => {
    if (!dragStartRef.current) return;
    e.stopPropagation();
    e.preventDefault();
    dragStartRef.current.captureTarget.releasePointerCapture?.(e.pointerId);
    const finalX = bubbleCenterXRef.current ?? dragStartRef.current.bubbleCenterX;
    const movedEnough = Math.abs(finalX - dragStartRef.current.bubbleCenterX) > DRAG_THRESHOLD_PX;
    const targetIndex = movedEnough ? getNearestTabIndex(finalX) : dragStartRef.current.tappedTabIndex;
    const targetTab = TABS[targetIndex];
    dragStartRef.current = null;
    bubbleCenterXRef.current = null;
    setIsDragging(false);
    setBubbleCenterX(null);

    // Не делать навигацию, если текущая страница и страница из to одна и та же, чтоб не засорять историю
    if (location.pathname.startsWith(targetTab.to)) {
      return;
    }

    // targetTab.onClick();

    // ? При нажатии на таб «Поиск» нужно просто открыть страницу /search-main-categories и скрыть на ней табы.
    // ? Сейчас, помимо этого, дополнительно открывается категория, которая находится под местом нажатия — там, где был таб «Поиск».

    // * Решение: нужно отложить навигацию на один тик/фрейм, чтобы табы оставались в DOM пока браузер не закончит генерацию click-события. Замените targetTab.onClick() на:
    // requestAnimationFrame(() => targetTab.onClick());

    // * или вот так
    setTimeout(() => targetTab.onClick(), 0);
  });

  useEventListener("pointerdown", handlePointerDown, { target: barRef });
  useEventListener("pointermove", handlePointerMove, { target: () => document });
  useEventListener("pointerup", handlePointerUp, { target: () => document });
  useEventListener("pointercancel", handlePointerUp, { target: () => document });

  const bubbleX = isDragging && bubbleCenterX !== null ? bubbleCenterX : defaultBubbleX;

  if (config.haveTabs || shouldHideButton) {
    return null;
  }

  return (
    /* 🌐 Весь нижний бар: фиксирован внизу экрана */
    <div className="fixed bottom-[1.375rem] left-0 right-0 z-[1000] pointer-events-none">
      <div
        ref={barRef}
        className="fixed bottom-5 left-1/2 -translate-x-1/2 z-50 pointer-events-auto "
        style={{ touchAction: "none" }}
      >
        {/* 🌟 Блюр-свечение позади всей навигации (декор) */}
        <div
          className="absolute -inset-4 rounded pointer-events-none"
          style={{
            background: "radial-gradient(ellipse at 50% 80%, rgba(160,170,255,0.12) 0%, transparent 65%)",
            filter: "blur(20px)",
          }}
        />

        {/* 🎨 Внешняя стеклянная рамка (обводка контейнера) */}
        <div
          className="relative p-[0.5px] rounded-[50px]"
          style={{
            background:
              "linear-gradient(135deg, rgba(255,255,255,0.75) 0%, rgba(255,255,255,0.12) 35%, rgba(190,200,255,0.35) 65%, rgba(255,255,255,0.55) 100%)",
          }}
        >
          {/* 🧊 Основной контейнер навигации — стеклянный фон */}
          <div
            ref={tabsContainerRef}
            className="relative flex items-center overflow-visible"
            style={{
              gap: TAB_GAP,
              height: 66,
              padding: "8px",
              borderRadius: 100,
              background:
                "linear-gradient(165deg, rgba(255,255,255,0.52) 0%, rgba(255,255,255,0.16) 45%, rgba(235,240,255,0.24) 100%)",
              backdropFilter: "blur(10px) saturate(200%) brightness(1.08)",
              WebkitBackdropFilter: "blur(50px) saturate(200%) brightness(1.08)",
              boxShadow: [
                "0 16px 48px -10px rgba(0,0,0,0.10)",
                "0 6px 16px -4px rgba(0,0,0,0.06)",
                "0 2px 4px rgba(0,0,0,0.03)",
                "inset 0 1.5px 0 rgba(255,255,255,0.60)",
                "inset 0 -0.5px 0 rgba(255,255,255,0.18)",
              ].join(", "),
            }}
          >
            {/* ✨ Верхняя светлая линия (эффект стекла) */}
            <div
              className="absolute top-0 left-[12%] right-[12%] pointer-events-none"
              style={{
                height: "0.5px",
                background:
                  "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.95) 25%, rgba(255,255,255,1) 50%, rgba(255,255,255,0.95) 75%, transparent 100%)",
              }}
            />

            {/* 🌈 Цветная переливная подложка (микро-градиент) */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                opacity: 0.05,
                background:
                  "linear-gradient(110deg, rgba(255,80,80,0.4) 0%, rgba(80,255,120,0.3) 30%, rgba(80,120,255,0.4) 60%, rgba(255,80,220,0.3) 100%)",
                mixBlendMode: "overlay",
              }}
            />

            {/* ➖ Центральная белая линия (для глубины) */}
            <div
              className="absolute top-[40%] left-[5%] right-[5%] pointer-events-none"
              style={{
                height: "1px",
                opacity: 0.08,
                background: "linear-gradient(90deg, transparent, rgba(255,255,255,1), transparent)",
              }}
            />

            {/* 🫧 Пузырь — движущийся фон под активной вкладкой */}
            <motion.div
              className="absolute top-[3px] bottom-[3px] pointer-events-none"
              style={{
                left: bubbleX - BUBBLE_WIDTH / 2,
                width: BUBBLE_WIDTH,
                height: 60,
                borderRadius: 50,
                background: "linear-gradient(165deg, rgba(255,255,255,0.58) 0%, rgba(255,255,255,0.18) 100%)",
                boxShadow: [
                  "0 4px 20px rgba(0,0,0,0.05)",
                  "0 1px 6px rgba(0,0,0,0.04)",
                  "inset 0 1.5px 0 rgba(255,255,255,0.75)",
                  "inset 0 -0.5px 0 rgba(255,255,255,0.25)",
                ].join(", "),
                border: "0.5px solid rgba(255,255,255,0.50)",
              }}
              animate={{
                left: bubbleX - BUBBLE_WIDTH / 2,
                scale: isDragging ? 1.05 : 1,
              }}
              transition={{
                left: isDragging ? { duration: 0 } : { type: "spring", stiffness: 230, damping: 30 },
                scale: { type: "spring", stiffness: 320, damping: 26 },
              }}
            />

            {/* 🔘 Кнопки-вкладки */}
            {TABS.map((tab, index) => {
              const active =
                location.pathname.startsWith(tab.to) || (tab.altRoute && location.pathname.startsWith(tab.altRoute));

              const isCartTab = index === CART_TAB_INDEX;
              const isProfileTab = index === PROFILE_TAB_INDEX;
              const showCartBadge = isCartTab && cartCount > 0;

              return (
                /* Контейнер одной вкладки */
                <div
                  key={tab.to}
                  className="relative flex flex-col items-center shrink-0 w-[70px] py-[10px] min-h-[56px]"
                >
                  {/* 📍 Иконка + текст (центрировано) */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none gap-1">
                    {/* 🖼️ Иконка вкладки */}
                    <IonIcon
                      icon={active ? tab.activeIcon : tab.icon}
                      style={{
                        fontSize: 26,
                        color: active ? "#fed700" : "rgba(0,0,0,0.32)",
                        filter: active ? "drop-shadow(0 1px 3px rgba(0,0,0,0.10))" : "none",
                      }}
                    />

                    {/* 🏷 Подпись вкладки */}
                    <span
                      className={`
                      text-[12px]
                      text-[rgba(0,0,0,0.32)]
                      tracking-[0.3px]
                      leading-[1]
                      ${active ? "font-semibold" : "font-normal"}
                    `}
                    >
                      {isCartTab && cartCount > 0 ? `${cartTotalValueWithShipping} ₽` : tab.label}
                    </span>
                  </div>
                  {/* 🔢 Бейдж количества товаров в корзине */}
                  {showCartBadge && (
                    <div className="absolute top-0 right-2 pointer-events-none flex items-center justify-center text-[#1a1a1a] text-[10px] font-bold min-width-[18px] rounded-3xl height-[18px] px-1 bg-[linear-gradient(135deg,#fed700_0%,#f0c000_100%)] shadow-[0_2px_6px_rgba(254,215,0,0.4)]">
                      {cartCount > 99 ? "99+" : cartCountFormatted}
                    </div>
                  )}
                  {/* 🔴 Бейдж непрочитанных чатов на профиле */}
                  {isProfileTab && isOrderChatEnabled && (
                    <ChatUnreadBadge count={chatsUnreadCount} className="absolute top-0 right-2" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TabNavigation;
